import { useContext, useMemo } from 'react'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import { LabRemoteConfigKeys } from 'constants/lab'
import { PROMO_CODE_PROMOTION, CTA_PROMOTION, FLIGHT_VOUCHER_PROMOTION, type LandingPagePromotion } from 'luxPlus/constants/promotions'
import type { PromoBannerItem } from 'luxPlus/components/LandingPage/LuxPlusPromoBannerCarousel'
import GeoContext from 'contexts/geoContext'

function hasRequiredFields(promo: Record<string, unknown>): boolean {
  return !!(promo.title && promo.subtitle && promo.label)
    && Array.isArray(promo.eligibleRegions) && promo.eligibleRegions.length > 0
}

function isExpired(promo: Record<string, unknown>): boolean {
  return promo.expiresAt != null && new Date(promo.expiresAt as string) < new Date()
}

function hasValidTypeFields(promo: Record<string, unknown>): boolean {
  switch (promo.type) {
    case PROMO_CODE_PROMOTION:
      return !!(promo.termsAndConditions && promo.promoCode)
    case CTA_PROMOTION:
      return !!(promo.ctaText && promo.ctaLink)
    case FLIGHT_VOUCHER_PROMOTION:
      return true
    default:
      return false
  }
}

function isValidPromo(promo: unknown): promo is LandingPagePromotion {
  if (typeof promo !== 'object' || promo === null) return false
  const p = promo as Record<string, unknown>
  return hasRequiredFields(p) && !isExpired(p) && hasValidTypeFields(p)
}

function isValidPromoBanner(promo: unknown): promo is PromoBannerItem {
  if (typeof promo !== 'object' || promo === null) return false
  const p = promo as Record<string, unknown>
  if (!p.label || !p.title || !p.ctaText || !p.ctaLink) return false
  if (isExpired(p)) return false
  return Array.isArray(p.eligibleRegions) && p.eligibleRegions.length > 0
}

/**
 * Lab remote config values are loosely typed (can be any JSON object),
 * so we need to validate the shape at runtime before using them.
 */
function parsePromos<
  T extends { eligibleRegions: Array<string> },
>(
  payload: App.LabRemoteConfigResult | undefined,
  validator: (item: unknown) => item is T,
  regionCode: string,
): Array<T> {
  if (!payload?.value) return []

  const value = payload.value
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return []

  const promos = (value as Record<string, unknown>).promos
  if (!Array.isArray(promos)) return []

  return promos.filter(validator).filter(
    (item) => item.eligibleRegions.includes(regionCode),
  )
}

export function useLuxPlusPromoCards(): Array<LandingPagePromotion> {
  const { currentRegionCode } = useContext(GeoContext)
  const payload = useLabRemoteConfig(LabRemoteConfigKeys.luxPlusPromoCardCarousel)

  return useMemo(
    () => parsePromos<LandingPagePromotion>(payload, isValidPromo, currentRegionCode),
    [payload, currentRegionCode],
  )
}

export function useLuxPlusPromoBanners(): Array<PromoBannerItem> {
  const { currentRegionCode } = useContext(GeoContext)
  const payload = useLabRemoteConfig(LabRemoteConfigKeys.luxPlusPromoBannerCarousel)

  return useMemo(
    () => parsePromos<PromoBannerItem>(payload, isValidPromoBanner, currentRegionCode),
    [payload, currentRegionCode],
  )
}
